/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Button Component
 * --------
 * Reusable button component used across the application.
 *
 * Supports different appearance modes:
 *  - primary
 *  - primary-bold
 *  - secondary
 *  - bordered
 *  - outlined
 *
 * Accepts a `small` prop for compact button sizes 
 * (used in mobile views or where space is limited).
 *
 * The component applies dynamic styles based on the
 * active theme (from ThemeContext), including:
 *  - button radius
 *  - primary/secondary theme colors
 *
 * Usage:
 *  <Button mode="primary">Click</Button>
 *  <Button mode="bordered" small>Save</Button>
 *
 * Any extra props (onClick, disabled, etc.) are passed
 * directly to the underlying <button> element.
 */

import React from 'react';
import { useTheme } from '../theme/ThemeContext';

function Button({
  mode = 'primary', // 'primary' | 'secondary' | 'bordered'
  children,
  className = '',
  small = false,
  ...props
}) {
  const { theme } = useTheme();

  let buttonStyles = {
    borderRadius: theme.buttonRadius,
    fontWeight: 600,
    padding: small ? '0.35em 1em' : '0.6em 1.5em',
    fontSize: small ? '0.85rem' : '1rem',
    border: 'none',
    transition: 'all 0.15s',
    boxShadow: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  switch (mode) {
    case 'primary-bold':
      buttonStyles.background = theme.primary;
      buttonStyles.color = '#fff';
      break;
    case 'primary':
      buttonStyles.background = '#fff';
      buttonStyles.color = theme.primary;
      buttonStyles.border = '1.5px solid #fff';
      break;
    case 'secondary':
      buttonStyles.background = theme.secondary;
      buttonStyles.color = '#fff';
      buttonStyles.border = '1.5px solid ' + theme.secondary;
      break;
    case 'bordered':
      buttonStyles.background = theme.primary;
      buttonStyles.color = '#fff';
      buttonStyles.border = '1.5px solid #fff';
      break;
    case 'outlined':
      buttonStyles.background = '#fff';
      buttonStyles.color = theme.primary;
      buttonStyles.border = '1.5px solid ' + theme.primary
      break;

    default:
      break;
  }

  return (
    <button
      className={`${className}`}
      style={buttonStyles}
      {...props}
    >
      {children}
    </button>
  );
}

export default Button;
